import React, { useEffect, useRef, useState } from "react";
import { Box, Container, Typography, Grid, Card, CardMedia, CardContent, useMediaQuery, useTheme } from "@mui/material";
import { gsap } from "gsap";
import { backsidebackgrounddesktop, backsidebackgroundmobile, bathroom, frenchdoors, gardientline, popupimg, temple } from "../../../assets";

const features = [
  {
    title: "Timeless Elegance",
    description:
      "Step into elegance with imported marble flooring that radiates sophistication & grandeur. Its polished finish and timeless charm redefine luxury in every room.",
    image: temple,
    label: "* IMPORTED MARBLE FLOORING",
  },
  {
    title: "Bright & Sleek",
    description:
      "Redefine elegance with sleek aluminum French doors. Designed to flood your home with natural light, these doors effortlessly blend modern aesthetics.",
    image: frenchdoors,
    label: "ALUMINUM FRENCH DOORS",
  },
  {
    title: "Elevated Comfort",
    description:
      "Experience everyday indulgence with premium sanitaryware that combines flawless design & top-tier functionality, creating spa-like comfort in your home.",
    image: bathroom,
    label: "PREMIUM SANITARYWARE",
  },
];

const LuxuryFeatures: React.FC = () => {
  const containerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    gsap.fromTo(
      containerRef.current,
      { opacity: 0, y: 50 },
      { opacity: 1, y: 0, duration: 1, ease: "power3.out", stagger: 0.3 }
    );
  }, []);

  return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "auto",
            backgroundImage: isMobile ? `url(${backsidebackgroundmobile})` : `url(${backsidebackgrounddesktop})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            px: { xs: 1, md: 5 },
          }}
        >
    <Box sx={{  minHeight: "auto", color: "white", py: 5, px: { xs: 1, sm: 5 } }}>
      <Container maxWidth="xl">
        {/* Section Title at Top Right */}
        <Box sx={{ textAlign: "right", mb: 5 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: "600",
              fontSize: "26px",
              background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
            }}
          >
            OPULENT TOUCH
          </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", my: 1, width: "100%" }}>
              <img src={gardientline} alt="2.5 BHK luxury apartment in Hyderabad by Kolla Group" style={{ width: "100%", maxWidth: "900px", marginRight: -3 }} />
            </Box>
        </Box>

        {/* Mobile View Layout */}
        {isMobile ? (
          <Grid container spacing={4} ref={containerRef}>
            {features.map((feature, index) => (
              <Grid
                item
                xs={12}
                key={index}
                sx={{
                  display: "flex",
                  flexDirection: index % 2 === 0 ? "row" : "row-reverse",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {/* Image */}
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: { xs: index % 2 === 0 ? "row" : "row-reverse", md: "column" }, 
                    alignItems: "center", 
                    gap: 2, 
                    mb: 6,
                  }}
                >
                  <Box sx={{ position: "relative", width: { xs: "45%", md: "100%" } , display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <img 
                        src={feature.image} 
                        alt={feature.label} 
                        style={{ width: "auto", borderRadius: "8px", height:"70px"  }} 
                    />          
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -38,
                        right: 0,
                        backgroundColor: "rgba(255, 248, 218, 0.84)", 
                        padding: "4px 12px",
                        borderRadius: "5px",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                        width: "auto",
                        
                      }}
                    >
                      <Box sx={{ width: "2px", height: "15px", backgroundColor: "#bf742c", marginRight: "8px", borderRadis: '4px' }} />
                        <Typography variant="h2" sx={{ fontWeight : "600", fontSize: "8px", color: "#000000" }}>
                          {feature.label}
                        </Typography>
                      </Box>
                  </Box>

                  {/* Content */}
                  <Box sx={{ width: { xs: "65%", md: "100%" } }}>
                    <Typography variant="h2" 
                      sx={{
                          fontWeight: "600",
                          fontSize: "14px",
                          background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          display: "inline-block",
                          marginBottom: "8px",
                        }}>
                          {feature.title}
                      </Typography>
                        <Box sx={{ width: "40px", height: "2px", background: "linear-gradient(90deg, #bf742c,rgb(224, 184, 147), #c58038)", marginRight: "8px" }} />
                        <Typography variant="h2"
                          sx={{
                            mt: 1,
                            fontSize: "10px",
                            fontWeight: "400",
                            color: '#f5f5f5',
                            textAlign: 'justify',
                          }}
                        >
                          {feature.description}
                        </Typography>
                    </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* Desktop View */
          <Grid container spacing={4} ref={containerRef}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ backgroundColor: "transparent", color: "white", boxShadow: "none", position: "relative",  }}>
                    <Box sx={{ position: "relative", borderRadius: "8px", textAlign: "center", width: "100%" }}>
                      <img 
                      src={feature.image} 
                      alt={feature.label} 
                      style={{ width: "auto", borderRadius: "8px", height: "120px", cursor: "pointer" }}
                      />    
                      {/* Label inside Image */}
                        <Box
                        sx={{
                        position: "absolute",
                        bottom: -60,
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "rgba(255, 248, 218, 0.9)", 
                        padding: "8px 12px",
                        borderRadius: "5px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "left",
                        whiteSpace: "nowrap",
                        width: "auto",
                        maxWidth: "100%",
                        textAlign: "left" 
                        }}
                        >
                        <Box sx={{ width: "2px", height: "20px", backgroundColor: "#bf742c", marginRight: "8px", borderRadius: '4px' }} />
                        <Typography variant="h2" sx={{ fontWeight: "600", fontSize: "12px", color: "#432818", textAlign: "left" }}>
                        {feature.label}
                        </Typography>
                        </Box>
                    </Box>

                  {/* Text Below Image */}
                  <CardContent sx={{  mt: 10 }}>
                  <Typography variant="h2" 
                      sx={{
                          fontWeight: "600",
                          fontSize: "20px",
                          background: "linear-gradient(90deg, #bf742c, #fff8da, #c58038)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          display: "inline-block",
                          marginBottom: "8px",
                        }}>
                          {feature.title}
                      </Typography>
                      <Box sx={{ width: "80px", height: "2px", background: "linear-gradient(90deg, #bf742c,rgb(224, 184, 147), #c58038)", marginRight: "8px" }} />
                        <Typography variant="h2"
                          sx={{
                            mt: 1,
                            fontSize: "14px",
                            fontWeight: "400",
                            color: '#f5f5f5',
                            textAlign: 'left',
                            letterSpacing: "0.5px",
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
    </Box>
  );
};

export default LuxuryFeatures;
